import { combine, createEvent, createStore, sample } from 'effector';
import { Fail, persist } from 'effector-storage/local';
import { reset } from 'patronum';

import { createOrderMutation } from '@/entities/order';

import { CreateOrderDto, Product } from '@/shared/api/generated/api.generated';
import { createDeclination } from '@/shared/lib/declination';
import { notified, NotifyFxOptions } from '@/shared/lib/effector/notifications';
import { clientStarted } from '@/shared/lib/startup/startup';

import { CART_STORAGE_KEY, CartItem, getCartUnitPrice, getQuantityLimit, isStoredCart } from './cart-data';

export type { CartItem } from './cart-data';

export const $cartList = createStore<CartItem[]>([]);
const cartStorageFailed = createEvent<Fail<Error>>();

persist({
  store: $cartList,
  key: CART_STORAGE_KEY,
  pickup: clientStarted.map(() => null),
  contract: isStoredCart,
  fail: cartStorageFailed,
});
export const $productAmountInCart = combine($cartList, (list) => list.length);

export const incrementAmount = createEvent<string>();
export const decrementAmount = createEvent<string>();
export const deleteProductFromCart = createEvent<string>();
export const createOrderEvent = createEvent<{
  user: CreateOrderDto['user'];
  requestId: string;
  consent: true;
  consentVersion: string;
}>();
export const $orderPending = createOrderMutation.$pending;
export const $orderError = createStore('')
  .reset(createOrderEvent)
  .on(createOrderMutation.finished.failure, (_, { error }) =>
    error instanceof Error
      ? error.message
      : 'Не удалось отправить заявку. Корзина и контакты сохранены — повторите попытку или позвоните +7 (343) 363-05-07.',
  );

export const changeAmount = createEvent<{ id: string; amount: number }>();

export const pushToCartList = createEvent<Product>();
export const cartProductsRefreshed = createEvent<Product[]>();
sample({
  clock: cartProductsRefreshed,
  source: $cartList,
  fn: (cart, products) => {
    const byId = new Map(products.map((product) => [product.id, product]));
    return cart.map((item) => {
      const product = byId.get(item.product.id);
      return product ? { product, amount: Math.min(item.amount, getQuantityLimit(product)) } : item;
    });
  },
  target: $cartList,
});

sample({
  clock: pushToCartList,
  source: $cartList,
  fn: (cartList, addedProduct) => {
    const existing = cartList.find((item) => item.product.id === addedProduct.id);
    if (!existing) return [...cartList, { product: addedProduct, amount: 1 }];
    return cartList.map((item) =>
      item.product.id === addedProduct.id
        ? { product: addedProduct, amount: Math.min(item.amount + 1, getQuantityLimit(addedProduct)) }
        : item,
    );
  },
  target: $cartList,
});

sample({
  clock: pushToCartList,
  fn: (): NotifyFxOptions => ({
    message: 'Товар добавлен в корзину',
    type: 'info',
  }),
  target: notified,
});

sample({
  clock: changeAmount,
  source: $cartList,
  filter: (_, { amount }) => Number.isFinite(amount),
  fn: (cartList, params) => {
    const newCart = cartList.map((item) => {
      if (item.product.id === params.id) {
        return {
          ...item,
          amount: Math.max(1, Math.min(getQuantityLimit(item.product), Math.floor(params.amount))),
        };
      }

      return item;
    });

    return newCart;
  },
  target: $cartList,
});

sample({
  clock: incrementAmount,
  source: $cartList,
  filter: (cartList, id) => {
    const item = cartList.find((card) => card.product.id === id);
    if (!item) {
      return false;
    }

    return item.amount < getQuantityLimit(item.product);
  },
  fn: (cartList, id) => {
    const newCart = cartList.map((item) => {
      if (item.product.id === id) {
        return {
          ...item,
          amount: item.amount + 1,
        };
      }

      return item;
    });

    return newCart;
  },
  target: $cartList,
});

sample({
  clock: decrementAmount,
  source: $cartList,
  filter: (cartList, id) => cartList.some((item) => item.product.id === id && item.amount > 1),
  fn: (cartList, id) => {
    const newCart = cartList.map((item) => {
      if (item.product.id === id) {
        return {
          ...item,
          amount: item.amount - 1,
        };
      }

      return item;
    });

    return newCart;
  },
  target: $cartList,
});

sample({
  clock: deleteProductFromCart,
  source: $cartList,
  fn: (cartList, id) => cartList.filter((item) => item.product.id !== id),
  target: $cartList,
});

sample({
  clock: deleteProductFromCart,
  fn: (): NotifyFxOptions => ({
    message: 'Товар убран из корзины',
    type: 'info',
  }),
  target: notified,
});

const getProductDeclension = createDeclination({
  one: 'товар',
  few: 'товара',
  many: 'товаров',
});

export const $cartTotalPrice = combine($cartList, (cartList) =>
  cartList.reduce((sum, item) => sum + (getCartUnitPrice(item.product) ?? 0) * item.amount, 0),
);

export const $productAmount = combine($cartList, (cartList) => {
  return getProductDeclension(cartList.length);
});

sample({
  clock: createOrderEvent,
  source: $cartList,
  fn: (cart, request) => {
    const products = cart.map((item) => ({ productId: item.product.id, quantity: item.amount }));

    return { ...request, products };
  },
  target: createOrderMutation.start,
});

export const $orderSent = createStore(false);

sample({
  clock: createOrderMutation.finished.success,
  fn: () => true,
  target: $orderSent,
});

reset({
  clock: [createOrderMutation.finished.success],
  target: $cartList,
});
